import React, { ForwardedRef, RefObject, useCallback, useMemo } from 'react';

import { Text } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { StreamTypes } from 'store/player/actions.types';
import { RBAssetType } from 'store/railsBuilder/reducer.types';
import { RecordingItem } from 'store/recordings/reducer.types';
import { selectLanguage } from 'store/user/selectors';

import { ContentType } from 'services/data-manager/types';

import getCorrectProgramStartAndEndTime from 'utils/getCorrectProgramStartAndEndTime';
import { makeTestDeeplinkTitle } from 'utils/getMockDeeplink';
import getOrdinal from 'utils/getOrdinary';
import {
    getDefaultMetadata,
    selectRecordingMetadataBaseOnLanguage,
} from 'utils/recordings';

import { DetailsPageParams, DetailsRequestUrlType } from 'screens/Detail/types';

import BaseTileImage from 'components/BaseTileImage';
import ContentMarkerIcons from 'components/ContentMarkerIcons';
import RibbonTouchable from 'components/CustomButtons/RibbonTouchable';
import Marker from 'components/Marker';
import { ImageSize, ImageType } from 'components/NagraImage/types';
import gRibbonItemStyles from 'components/Ribbon/RibonItem/styles';
import ProgramLogo from 'components/Ribbon/components/ProgramLogo';
import { TileTitle } from 'components/Ribbon/components/TileTitle';
import { PLACEHOLDER_ICON_CENTER } from 'components/Ribbon/constants';
import useParentalRating from 'components/Ribbon/hooks/useParentalRating';
import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';

type Props<T = RecordingItem> = {
    item: T;
    isCollection?: boolean;
    testID?: string;
    index?: number;
    totalNumberOfItems?: number;
} & OnUavAnalyticsProps &
    StandardUavProps;

const {
    ACCESSIBILITY: {
        ACCESSIBILITY_COLLECTION_FIRST_ASSET,
        STRIPE: { ACCESSIBILITY_STRIPE_FIRST_ASSET },
    },
} = LANGUAGE_KEYS;

const RecordingRibbon = (
    {
        item,
        onRailsSelection,
        isCollection = false,
        railId,
        templateId,
        testID,
        index = 0,
        totalNumberOfItems,
    }: Props,
    forwardedRef?: ForwardedRef<any | undefined>,
) => {
    const recordingRef = forwardedRef as RefObject<any> | undefined;
    const { t } = useTranslation();
    const navigation =
        useNavigation<
            StackNavigationProp<RouteNavigationParams, SCREEN.DETAILS>
        >();

    const currentLanguage = useSelector(selectLanguage);
    const { primary } = useSelector(selectLayoutConfig);

    const recordingMetadata = useMemo(() => {
        const defaultRecordingMetadata = getDefaultMetadata(item);
        const metadataBySelectedLanguage =
            selectRecordingMetadataBaseOnLanguage(
                item.metadata,
                currentLanguage,
            );

        return { ...defaultRecordingMetadata, ...metadataBySelectedLanguage };
    }, [item.metadata, currentLanguage]);

    const parentalRating = useParentalRating(recordingMetadata);

    const { programStartTime, programEndTime } =
        getCorrectProgramStartAndEndTime(recordingMetadata);

    const handlePress = useCallback(() => {
        if (recordingMetadata?.editorial?.id) {
            onRailsSelection?.({
                id: recordingMetadata.editorial.id,
                playbackType: StreamTypes.npvr,
                index,
            });
        }

        const navigationParams: DetailsPageParams = {
            id: item.contentId,
            seriesId: recordingMetadata?.editorial?.seriesRef,
            channelId: recordingMetadata?.serviceRef,
            episode: recordingMetadata?.episodeNumber,
            season: recordingMetadata?.SeasonNumber,
            type: recordingMetadata?.editorial?.seriesRef
                ? RBAssetType.SERIES
                : RBAssetType.PROGRAMME,
            playbackType: undefined,
            filterType: DetailsRequestUrlType.PROGRAM,
        };

        navigation.navigate(SCREEN.DETAILS, {
            ...navigationParams,
            railId,
            templateId,
        });
    }, [
        navigation,
        item,
        onRailsSelection,
        railId,
        templateId,
        recordingMetadata,
        index,
    ]);

    const accessibilityLabel = useMemo(() => {
        let label = '';

        if (recordingMetadata?.SeasonNumber) {
            label += `${t('HOVER_SEASON')}${recordingMetadata.SeasonNumber} `;
        }

        if (recordingMetadata?.episodeNumber) {
            label += `${t('HOVER_EPISODE')}${recordingMetadata.episodeNumber} `;
        }

        label += `${recordingMetadata?.title || ''} `;

        const firstAssetKey = isCollection
            ? ACCESSIBILITY_COLLECTION_FIRST_ASSET
            : ACCESSIBILITY_STRIPE_FIRST_ASSET;
        const orderAssetKey = 'ACCESSIBILITY_STRIPE_%NUMBER%_ASSET';

        if (index === 0) {
            label += t(firstAssetKey, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        } else {
            label += t(orderAssetKey, {
                number: getOrdinal(index + 1),
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        }

        return label;
    }, [recordingMetadata, index, totalNumberOfItems, t, isCollection]);

    return (
        <RibbonTouchable
            ref={recordingRef}
            testID={testID}
            metadataType={ContentType.NPVR}
            isCollection={isCollection}
            onPress={handlePress}
            railId={railId}
            accessibilityLabel={accessibilityLabel}
            RibbonContentComponent={
                <>
                    {!!recordingMetadata?.editorial?.id && (
                        <BaseTileImage
                            style={gStyles.flex}
                            resizeMode="cover"
                            contentId={recordingMetadata?.editorial?.id}
                            contentType={item.contentType}
                            imageType={ImageType.BANNER}
                            width={ImageSize.LANDSCAPE.width}
                            placeHolder={PLACEHOLDER_ICON_CENTER}
                        />
                    )}
                    <ProgramLogo channelId={recordingMetadata?.serviceRef} />
                    {!!(programStartTime && programEndTime) && (
                        <Marker
                            startTime={programStartTime}
                            endTime={programEndTime}
                            bgColor={primary}
                            style={gRibbonItemStyles.marker}
                            textStyle={gRibbonItemStyles.markerText}
                        />
                    )}
                    <ContentMarkerIcons
                        parentalRating={parentalRating}
                        isLimitedToOne
                    />
                </>
            }
            RibbonInfoComponent={
                <TileTitle
                    title={
                        <>
                            {!!recordingMetadata?.SeasonNumber && (
                                <Text>
                                    {`${t('HOVER_SEASON')}${
                                        recordingMetadata.SeasonNumber
                                    } `}
                                </Text>
                            )}
                            {!!recordingMetadata?.episodeNumber && (
                                <Text>
                                    {`${t('HOVER_EPISODE')}${
                                        recordingMetadata?.episodeNumber
                                    } `}
                                </Text>
                            )}
                            {makeTestDeeplinkTitle(
                                recordingMetadata,
                                recordingMetadata?.title || '',
                            )}
                        </>
                    }
                />
            }
        />
    );
};

export default React.forwardRef(RecordingRibbon);
